import { CaretDown } from '@creditornot/cb-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import GirlShrugging from 'common/illustrations/GirlShrugging';

import * as S from './DataTable.styles';
import { DataTableControls } from './DataTableControls';
import { RowImages } from './RowImages';
import { DataTableState } from './useDataTable';
import { ShapedLine } from '../../libs/googleSheets/models';
import { isOptionLineType } from '../../utils';

type Props = {
  state: DataTableState;
};

const getRowIcon = (item: Partial<ShapedLine>, hasError: boolean): JSX.Element => {
  if (hasError) {
    return <S.ErrorIcon />;
  }

  return item?.id || (isOptionLineType(item) && item?.option_value_id) ? <S.UpdateIcon /> : <S.AdditionIcon />;
};

export const DataTable = ({ state }: Props) => {
  const intl = useIntl();
  const [expandedRowLineNumber, setExpandedRowLineNumber] = useState<number | null>(null);

  useEffect(() => {
    setExpandedRowLineNumber(null);
  }, [state.dataToDisplay]);

  const handleRowExpandClick = useCallback((rowLineNumber: number) => {
    setExpandedRowLineNumber(prevExpandedRowLineNumber =>
      prevExpandedRowLineNumber === rowLineNumber ? null : rowLineNumber,
    );
  }, []);

  return (
    <S.Root>
      <DataTableControls state={state} />
      <S.TableHorizontalScrollContainer>
        {state.dataToDisplay.length ? (
          <S.Table>
            <S.TableHeader>
              {/* Fixed empty header to account for the error (collapsible icon) cell */}
              <S.TableCell $fixedWidth />

              {state.columnsToDisplay[state.currentView].map(({ label }, index) => (
                <S.TableCell key={index}> {intl.formatMessage({ id: label })}</S.TableCell>
              ))}
            </S.TableHeader>
            {state.dataToDisplay.map((rowObject, index) => {
              const maybeErrorMessage = state.getRowErrorMessage(rowObject.rowNumber);
              return (
                <>
                  <S.TableRow
                    $hasError={!!maybeErrorMessage}
                    $isExpanded={expandedRowLineNumber === rowObject.rowNumber}
                    key={index}
                    onClick={() => handleRowExpandClick(rowObject.rowNumber)}
                  >
                    <S.TableCell>
                      {!!maybeErrorMessage ? (
                        <S.ArrowIconContainer $isExpanded={expandedRowLineNumber === rowObject.rowNumber}>
                          <CaretDown />
                        </S.ArrowIconContainer>
                      ) : null}
                    </S.TableCell>
                    {state.columnsToDisplay[state.currentView].map(({ value }, index) => {
                      return (
                        <S.TableCell key={index} $fixedWidth={value === 'rowNumber' || value === 'status'}>
                          {value === 'status' ? (
                            getRowIcon(rowObject, !!maybeErrorMessage)
                          ) : value === 'images' ? (
                            <RowImages images={(rowObject as Record<typeof value, string>)?.images} />
                          ) : (
                            (rowObject as Record<typeof value, React.ReactNode>)?.[value]?.toString() ?? '-'
                          )}
                        </S.TableCell>
                      );
                    })}
                  </S.TableRow>
                  {maybeErrorMessage && expandedRowLineNumber === rowObject.rowNumber && (
                    <S.ErrorRow>
                      <S.ErrorMessageCell>
                        {maybeErrorMessage?.map(errorMessage => <p>{errorMessage}</p>)}
                      </S.ErrorMessageCell>
                    </S.ErrorRow>
                  )}
                </>
              );
            })}
          </S.Table>
        ) : (
          <S.EmptyResults>
            <S.Illustration>
              <GirlShrugging />
            </S.Illustration>
            <S.EmptyText>
              <FormattedMessage id="shared-menu.editor.importer.table-view.no-items" />
            </S.EmptyText>
          </S.EmptyResults>
        )}
      </S.TableHorizontalScrollContainer>
    </S.Root>
  );
};
